import { subject } from "@casl/ability";
import axios from "axios";
import isEmpty from "lodash/isEmpty";

import { getErrorDetails } from "api/helpers/getErrorDetails";
import {
  addPaginationAndSort,
  getPaginationInfoFromHeaders,
  ORDER_ASC,
  PaginatedList,
} from "api/helpers/pagination";
import { paramsSerializer } from "api/helpers/paramsSerializer";
import { portalApi, TAGS } from "api/rtkApi";
import { CountryCode } from "models/CountryCode";
import { SUBJECT_BANK } from "models/role/subject";
import { isUUID } from "utils";

import type { Order } from "models/helpers";
import type { RTKMeta } from "models/RTKMeta";

import type { Bank } from "../models/banks";

export type GetBanksRequest = {
  limit: number;
  order: Order;
  orderBy: keyof Bank;
  page: number;
  search: string | null;
};

export async function getBanks(
  qsParams: GetBanksRequest,
  options?: any
): Promise<PaginatedList<Bank>> {
  const { search } = qsParams;
  const params = { ...addPaginationAndSort<Bank>(qsParams) };

  if (!isEmpty(search)) {
    Object.assign(params, { search: search });
  }

  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/banks",
      params,
      paramsSerializer,
    });

    const { data = {}, headers: responseHeaders = {} } = response;

    const { totalItems, currentPage, currentLimit } =
      getPaginationInfoFromHeaders(responseHeaders);

    return {
      list: (data ?? []).map((bank: Bank) => subject(SUBJECT_BANK, bank)),
      totalItems,
      currentPage,
      currentLimit,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

type GetBanksForAutocompleteResponse = {
  banks: Bank[];
  totalItems?: number;
};

type GetBanksForAutocompleteRequest = {
  search?: string;
};

export type GetCustomersResponse = CountryCode[];

const BANKS_AUTOCOMPLETE_RESULTS_LIMIT = 100;

const extendedApi = portalApi.injectEndpoints({
  endpoints: (build) => ({
    getBanksForAutocomplete: build.query<
      GetBanksForAutocompleteResponse,
      GetBanksForAutocompleteRequest
    >({
      query: ({ search }) => ({
        url: "banks",
        params: {
          limit: BANKS_AUTOCOMPLETE_RESULTS_LIMIT,
          "order[name]": ORDER_ASC,
          page: 1,
          ...(typeof search === "string" && isUUID(search)
            ? {
                id: search,
              }
            : { search }),
        },
      }),
      providesTags: (_, __, { search }) => [
        { type: TAGS.BANKS_AUTOCOMPLETE, id: search },
      ],
      transformResponse: (data: Bank[], meta: RTKMeta) => {
        const totalItems = Number(meta.response.headers.get("total-items"));

        return {
          totalItems,
          banks: data,
        };
      },
    }),
    getProcessableCountries: build.query<GetCustomersResponse, void>({
      query: () => ({
        url: "/banks/countries/processable",
      }),
      providesTags: [TAGS.COUNTRIES],
    }),
  }),
});

export const {
  useLazyGetBanksForAutocompleteQuery,
  useGetProcessableCountriesQuery,
} = extendedApi;
